import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { InputGroup, InputGroupAddon } from '@/components/ui/input-group'
import { cn } from '@/lib/utils'
import { CheckIcon, SearchIcon } from 'lucide-react'

function Command({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='command'
      className={cn(
        'flex size-full flex-col overflow-hidden rounded-xl! bg-popover p-1 text-popover-foreground',
        className
      )}
      {...props}
    />
  )
}

function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = false,
  ...props
}: Omit<React.ComponentProps<typeof Dialog>, 'children'> & {
  title?: string
  description?: string
  className?: string
  showCloseButton?: boolean
  children: React.ReactNode
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className='sr-only'>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn('top-1/3 translate-y-0 overflow-hidden rounded-xl! p-0', className)}
        showCloseButton={showCloseButton}
      >
        {children}
      </DialogContent>
    </Dialog>
  )
}

type CommandInputProps = React.ComponentProps<'input'> & {
  onValueChange?: (value: string) => void
}

function CommandInput({ className, onChange, onValueChange, ...props }: CommandInputProps) {
  return (
    <div data-slot='command-input-wrapper' className='p-1 pb-0'>
      <InputGroup className='h-8! rounded-lg! border-input/30 bg-input/30 shadow-none! *:data-[slot=input-group-addon]:pl-2!'>
        <input
          data-slot='command-input'
          className={cn(
            'w-full text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          onChange={(event) => {
            onChange?.(event)
            onValueChange?.(event.target.value)
          }}
          {...props}
        />
        <InputGroupAddon>
          <SearchIcon className='size-4 shrink-0 opacity-50' />
        </InputGroupAddon>
      </InputGroup>
    </div>
  )
}

function CommandList({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='command-list'
      className={cn(
        'clean-scrollbar max-h-72 scroll-py-1 overflow-x-hidden overflow-y-auto outline-none',
        className
      )}
      {...props}
    />
  )
}

function CommandEmpty({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='command-empty'
      className={cn('py-6 text-center text-sm', className)}
      {...props}
    />
  )
}

type CommandGroupProps = React.ComponentProps<'div'> & {
  heading?: React.ReactNode
}

function CommandGroup({ className, heading, children, ...props }: CommandGroupProps) {
  return (
    <div
      data-slot='command-group'
      className={cn('overflow-hidden p-1 text-foreground', className)}
      {...props}
    >
      {heading ? (
        <div className='px-2 py-1.5 text-xs font-medium text-muted-foreground'>{heading}</div>
      ) : null}
      {children}
    </div>
  )
}

function CommandSeparator({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='command-separator'
      className={cn('-mx-1 h-px w-auto bg-border', className)}
      {...props}
    />
  )
}

type CommandItemProps = Omit<React.ComponentProps<'button'>, 'onSelect'> & {
  checked?: boolean
  onSelect?: () => void
}

function CommandItem({
  className,
  children,
  checked = false,
  onClick,
  onSelect,
  ...props
}: CommandItemProps) {
  return (
    <button
      type='button'
      data-slot='command-item'
      data-checked={checked}
      className={cn(
        'group/command-item relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none disabled:pointer-events-none disabled:opacity-50 hover:bg-muted hover:text-foreground data-[checked=true]:bg-muted data-[checked=true]:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
        className
      )}
      onClick={(event) => {
        onClick?.(event)
        if (!event.defaultPrevented) {
          onSelect?.()
        }
      }}
      {...props}
    >
      {children}
      <CheckIcon className='ml-auto opacity-0 group-has-data-[slot=command-shortcut]/command-item:hidden group-data-[checked=true]/command-item:opacity-100' />
    </button>
  )
}

function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot='command-shortcut'
      className={cn(
        'ml-auto text-xs tracking-widest text-muted-foreground group-data-selected/command-item:text-foreground',
        className
      )}
      {...props}
    />
  )
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
